/**
 * Which "joined" / "left" notices a presence change earns — pure, so the rules
 * can be tested without a socket (useParticipantAlerts sends them).
 *
 * A screen-share-only session (contract §8, decision 7) is announced in its
 * own words — "{name} joined for screen share" in place of the generic
 * joined notice, "{name} left screen share" — under the same rules as anyone:
 * the in-meeting switch, the account's alert settings, and silence above 50
 * people. Except that hosts and co-hosts are always told one joined: they are
 * who has to approve it.
 */
import type { PresenceRoster } from './presence-roster'
import { isShareOnlyPeer } from './share-only'

/** Past this many at once (a meeting opening, a breakout closing), one toast with the count. */
export const MAX_NAMED = 3
/**
 * Above this many in the room, comings and goings are not announced: in a large
 * meeting they are constant and would bury every other notice. Other meeting
 * apps do the same.
 */
export const QUIET_ABOVE = 50

export type RosterChange = { id: string; name: string; shareOnly: boolean }

/**
 * Who arrived and who went. Read off the reconciled roster, not the raw diff:
 * Presence sends a camera/mic/hand change as a leave of the old meta plus a
 * join of the new one, and a key that survives is someone who never left.
 */
export function rosterChanges(prev: PresenceRoster, next: PresenceRoster, selfId: string | null) {
  const joined: RosterChange[] = []
  const left: RosterChange[] = []
  const change = (roster: PresenceRoster, id: string): RosterChange => {
    const metas = roster.get(id)
    const meta = metas?.[metas.length - 1]
    const name = meta?.user_name
    return { id, name: typeof name === 'string' ? name : '', shareOnly: isShareOnlyPeer(id, { share_only: meta?.share_only }) }
  }
  for (const id of next.keys()) if (id !== selfId && !prev.has(id)) joined.push(change(next, id))
  for (const id of prev.keys()) if (id !== selfId && !next.has(id)) left.push(change(prev, id))
  return { joined, left }
}

export type NoticePart = { key: string; vars: Record<string, string | number> }
/** One toast: its parts joined by ", ". `personal` shows it whatever the in-meeting switch says. */
export type ParticipantNotice = { parts: NoticePart[]; personal: boolean }

export type NoticeGates = {
  /** Everyone in the room right now, this client included. */
  rosterSize: number
  /** The in-meeting "Notifications" switch. */
  inMeetingNotifications: boolean
  /** The account's join (or leave) alert setting, do-not-disturb applied. */
  accountAllows: boolean
  /** Host or co-host: always told a screen-share session joined. */
  viewerIsModerator: boolean
}

export function participantNotices(
  kind: 'join' | 'leave',
  people: readonly RosterChange[],
  gates: NoticeGates,
  fallbackName: string,
): ParticipantNotice[] {
  const ordinary = people.filter((p) => !p.shareOnly)
  const shares = people.filter((p) => p.shareOnly)
  const allowed = gates.rosterSize <= QUIET_ABOVE && gates.inMeetingNotifications && gates.accountAllows
  const name = (p: RosterChange) => p.name || fallbackName
  const out: ParticipantNotice[] = []

  if (ordinary.length > 0 && allowed) {
    out.push({
      personal: false,
      parts:
        ordinary.length > MAX_NAMED
          ? [{ key: kind === 'join' ? 'meetingScreen.participantsJoined' : 'meetingScreen.participantsLeft', vars: { n: ordinary.length } }]
          : ordinary.map((p) => ({
              key: kind === 'join' ? 'meetingScreen.participantJoined' : 'meetingScreen.participantLeft',
              vars: { name: name(p) },
            })),
    })
  }

  const moderatorMustKnow = kind === 'join' && gates.viewerIsModerator
  if (shares.length > 0 && (allowed || moderatorMustKnow)) {
    out.push({
      personal: moderatorMustKnow,
      parts: shares.map((p) => ({
        key: kind === 'join' ? 'meetingScreen.participantJoinedForShare' : 'meetingScreen.participantLeftShare',
        vars: { name: name(p) },
      })),
    })
  }

  return out
}
